import * as angular from 'angular';
import { Dictionary, ReprocessingTaskOptionsOverride, SaveSpotValuesDto, SpotValue, SpotValuesCount } from '../../models';

import { IRequestService } from '../../services';

export interface ISpotValuesService {
    get(tagGuid: string): angular.IPromise<SpotValue[]>;
    /**
     * Always forces refresh
     * @param tagGuid 
     * @param beginDate 
     * @param endDate 
     */
    getBetweenDates(tagGuid: string, beginDate: string, endDate: string): angular.IPromise<SpotValue[]>;
    getAtDateForList(tagGuids: string[], beginDate: string, force?: boolean): angular.IPromise<Dictionary<SpotValue[]>>;
    getBetweenDatesForList(tagGuids: string[], beginDate: string, endDate: string, force?: boolean): angular.IPromise<SpotValue[]>;
    getBetweenDatesWithLastBefore(tagGuids: string[], beginDate: string, endDate: string, force?: boolean): angular.IPromise<SpotValue[]>;
    createOrUpdateOne(value: SpotValue): angular.IPromise<SpotValue>;
    createOrUpdateMany(values: SpotValue[]): angular.IPromise<SpotValue[]>;
    delete(tagGuid: string, dates: string[]): angular.IPromise<number>;
    count(beginDate: string, endDate: string): angular.IPromise<SpotValuesCount>;
    save(values: SaveSpotValuesDto, launchTask: boolean, taskOptionsOverride?: ReprocessingTaskOptionsOverride): angular.IPromise<number>;
}

export class SpotValuesService implements ISpotValuesService {
    static serviceId = 'spotValuesService';

    private readonly _url = 'spotValues/';

    static $inject = ['requestService'];

    constructor(private _requestService: IRequestService) {
    }

    createOrUpdateOne(value: SpotValue): angular.IPromise<SpotValue> {
        return this._requestService.postAtV1(`${this._url}update`, value);
    }

    createOrUpdateMany(values: SpotValue[]): angular.IPromise<SpotValue[]> {
        return this._requestService.postAtV1(`${this._url}updateMany`, values);
    }

    delete(tagGuid: string, dates: string[]): angular.IPromise<number> {
        return this._requestService.postAtV1(`${this._url}${tagGuid}`, dates);
    }

    get(tagGuid: string): angular.IPromise<SpotValue[]> {
        this._requestService.cleanCacheAtV1(`${this._url}${tagGuid}`);
        return this._requestService.getAtV1(`${this._url}${tagGuid}`);
    }

    getBetweenDates(tagGuid: string, beginDate: string, endDate: string): angular.IPromise<SpotValue[]> {
        const url = `${this._url}getBetweenDates/${tagGuid}`;
        const params = { beginDate, endDate };
        this._requestService.cleanCacheAtV1(url, params);
        return this._requestService.getAtV1(url, params);
    }
    getBetweenDatesForList(tagGuids: string[], beginDate: string, endDate: string, force?: boolean): angular.IPromise<SpotValue[]> {
        const url = `${this._url}getBetweenDatesForList`;
        const params = { beginDate, endDate };
        if (force) {
            this._requestService.cleanCachedPostValue(url, tagGuids, params);
        }
        return this._requestService.cachedPostAtV1(url, tagGuids, params);
    }

    getBetweenDatesWithLastBefore(tagGuids: string[], beginDate: string, endDate: string, force: boolean = false): angular.IPromise<SpotValue[]> {
        const url = `${this._url}getBetweenDatesWithLastBefore`;
        const params = { beginDate, endDate };
        if (force) {
            this._requestService.cleanCachedPostValue(url, tagGuids, params);
        }
        return this._requestService.cachedPostAtV1(url, tagGuids, params);
    }

    getAtDateForList(tagGuids: string[], beginDate: string, force: boolean = false): angular.IPromise<Dictionary<SpotValue[]>> {
        const url = `${this._url}getAtDateForList`;
        const params = { beginDate };
        if (force) {
            this._requestService.cleanCachedPostValue(url, tagGuids, params);
        }
        return this._requestService.cachedPostAtV1(url, tagGuids, params);
    }


    count(beginDate: string, endDate: string): angular.IPromise<SpotValuesCount> {
        const url = `${this._url}count`;
        const params = { beginDate, endDate };
        this._requestService.cleanCacheAtV1(url, params);
        return this._requestService.getAtV1(url, params);
    }

    save(values: SaveSpotValuesDto, launchTask: boolean, taskOptionsOverride: ReprocessingTaskOptionsOverride = null): angular.IPromise<number> {
        return this._requestService.postAtV1(`${this._url}/save`, {
            launchTask: launchTask,
            taskOptionsOverride: taskOptionsOverride,
            values: values,
        });
    }
}